import type { GanttZoomLevel } from '../../define.js';
import type { BarState } from '../../extensions/types.js';

export interface PositionCacheKey {
  readonly rowId: string;
  readonly zoom: GanttZoomLevel;
}

interface CacheEntry {
  readonly state: BarState;
  readonly x: number;
  readonly width: number;
  readonly transformVersion: number;
}

/**
 * Memoises the projected (`x`, `width`) of a bar for a given rowId at a
 * given zoom level. Invalidation is explicit — the scroller calls
 * {@link invalidateRow} on Y.js updates and {@link invalidateAll} when
 * the transform registry version bumps (priority-changed /
 * extension-added). Zoom-level changes invalidate automatically because
 * the zoom is part of the cache key.
 */
export class PositionCache {
  private readonly entries = new Map<string, CacheEntry>();

  get(key: PositionCacheKey, transformVersion: number): CacheEntry | undefined {
    const id = toId(key);
    const entry = this.entries.get(id);
    if (!entry) return undefined;
    if (entry.transformVersion !== transformVersion) {
      this.entries.delete(id);
      return undefined;
    }
    return entry;
  }

  set(
    key: PositionCacheKey,
    entry: Omit<CacheEntry, 'transformVersion'>,
    transformVersion: number
  ): void {
    this.entries.set(toId(key), { ...entry, transformVersion });
  }

  invalidateRow(rowId: string): void {
    for (const key of this.entries.keys()) {
      if (key.startsWith(`${rowId}:`)) this.entries.delete(key);
    }
  }

  invalidateAll(): void {
    this.entries.clear();
  }

  get size(): number {
    return this.entries.size;
  }
}

function toId(key: PositionCacheKey): string {
  return `${key.rowId}:${key.zoom}`;
}
